import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import useUnits from '../../../hooks/useUnits'
import { AccountingId } from '../../../config/finance/invoiceConfig'
import { UnitDto } from '../../../context/GlobalContext'
import EntityField, { CreateDialogProps } from './EntityField'
import { EntityLabelType } from './EntityLabel'
import { useFormikContext } from 'formik'
import CreateDimensionDialog, { AddDimensionValues } from './CreateDimensionDialog'
import DimensionFields, { DimensionFieldTuple, DimensionPivotValues } from './DimensionFields'
import { AccordionHeader, AccordionItem, AccordionPanel } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'

type Props = {
    styles: Record<'formRow', string>
    fieldName: string
    initialValues?: AddDimensionValues
    selectedUnit?: string
}

// noinspection JSUnusedLocalSymbols
function Fields({ styles, fieldName, initialValues, unit }: Props & { unit: UnitDto }): ReactElement {
    const { units } = useUnits()
    const { t: translate } = useTranslation()

    const {
        values: { [fieldName]: values },
        setFieldValue,
    } = useFormikContext<DimensionPivotValues>()

    if ((unit.dimensions || []).length === 0) {
        return <></>
    }
    return (
        <AccordionItem value="accounting">
            <AccordionHeader size="large">
                {translate('accounting.dimensions.title', {
                    count: units.length,
                    unit: unit.name,
                    accounting: unit.accounting,
                })}
            </AccordionHeader>
            <AccordionPanel>
                <div className={styles.formRow}>
                    <DimensionFields unit={unit}>
                        {([dimension, property]: DimensionFieldTuple) => (
                            <EntityField
                                key={`accounting-${unit.key}-${dimension}`}
                                unit={unit}
                                labelType={EntityLabelType.None}
                                externalType={dimension}
                                value={values?.[unit.key]?.[property]}
                                setFieldValue={(item: AccountingId) =>
                                    setFieldValue(`${fieldName}.${unit.key}.${property}`, item)
                                }
                                createDialog={(props: CreateDialogProps) => (
                                    <CreateDimensionDialog
                                        key={`create-dimension-dialog-${unit.key}-${dimension}`}
                                        externalType={dimension}
                                        initialValues={initialValues}
                                        unit={unit}
                                        {...props}
                                    />
                                )}
                            />
                        )}
                    </DimensionFields>
                </div>
            </AccordionPanel>
        </AccordionItem>
    )
}

export default function MultiUnitDimensionFields(props: Props): ReactElement {
    const { units } = useUnits()
    // The map() statement triggers frequent re-renders. Therefore, memoize it.
    return useMemo(
        () => (
            <>
                {(units || [])
                    .filter(unit => props.selectedUnit === undefined || unit.key === props.selectedUnit)
                    .map((unit: UnitDto) => (
                        <Fields key={`dimension-fields-${unit.key}-${props.fieldName}`} unit={unit} {...props} />
                    ))}
            </>
        ),
        [units, props],
    )
}
